import * as React from 'react';

import { useQueue } from '@uidotdev/usehooks';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  ToggleButton as AriaToggleButton,
  ToggleButtonGroup as AriaToggleButtonGroup,
  ToggleButtonRenderProps,
  composeRenderProps,
  PressEvent,
  type ToggleButtonGroupProps as AriaToggleButtonGroupProps,
  type ToggleButtonProps as AriaToggleButtonProps,
} from 'react-aria-components';

import { cn, isDefined } from '../../../lib';
import { ThemeColor } from '../../../types/theme';
import { Row } from '../containers';

const toggleVariants = cva(
  [
    'group/toggle relative inline-flex items-center justify-center bg-white text-sm whitespace-nowrap ring-offset-neutral/10 transition-all',
    'rac-hover:bg-custom-color/10 rac-hover:text-custom-color/80',
    'rac-selected:bg-custom-color rac-selected:text-abs-white',
    'rounded-md',

    /* Vertical Grouping - using data attributes instead of complex selectors */
    'group-data-[orientation=vertical]/togglegroup:border-x group-data-[orientation=vertical]/togglegroup:border-neutral/20',
    'group-data-[orientation=vertical]/togglegroup:rounded-none',
    'group-data-[orientation=vertical]/togglegroup:first:border-t',
    'group-data-[orientation=vertical]/togglegroup:first:rounded-tl-md',
    'group-data-[orientation=vertical]/togglegroup:first:rounded-tr-md',
    'group-data-[orientation=vertical]/togglegroup:last:border-b',
    'group-data-[orientation=vertical]/togglegroup:last:rounded-bl-md',
    'group-data-[orientation=vertical]/togglegroup:last:rounded-br-md',

    /* Horizontal Grouping - using data attributes instead of complex selectors */
    'group-data-[orientation=horizontal]/togglegroup:border-y group-data-[orientation=horizontal]/togglegroup:border-neutral/20',
    'group-data-[orientation=horizontal]/togglegroup:rounded-none',
    'group-data-[orientation=horizontal]/togglegroup:first:border-l',
    'group-data-[orientation=horizontal]/togglegroup:first:rounded-tl-md',
    'group-data-[orientation=horizontal]/togglegroup:first:rounded-bl-md',
    'group-data-[orientation=horizontal]/togglegroup:last:border-r',
    'group-data-[orientation=horizontal]/togglegroup:last:rounded-tr-md',
    'group-data-[orientation=horizontal]/togglegroup:last:rounded-br-md',

    /* Hover states for grouped items */
    'group-data-[orientation=vertical]/togglegroup:rac-hover:border-custom-color/10',
    'group-data-[orientation=vertical]/togglegroup:rac-selected:border-custom-color',
    'group-data-[orientation=horizontal]/togglegroup:rac-hover:border-custom-color/10',
    'group-data-[orientation=horizontal]/togglegroup:rac-selected:border-custom-color',

    /* Disabled */
    'rac-disabled:pointer-events-none',
    /* Focus Visible */
    'rac-focus-visible:ring-2 rac-focus-visible:ring-custom-color rac-focus-visible:ring-offset-2 rac-focus-visible:outline-none',
    /* Resets */
    'focus-visible:outline-none',
    /* Group specific */
    'group-data-[orientation=vertical]/togglegroup:w-full',
  ],
  {
    variants: {
      size: {
        default: 'min-h-input px-2.5 py-1.5',
        sm: 'px-3 py-1',
        lg: 'px-5 py-2.5',
        xl: [
          'h-13 border-2 px-5 py-3',
          'group-data-[orientation=vertical]/togglegroup:border-x-2',
          'group-data-[orientation=horizontal]/togglegroup:border-y-2',
          'group-data-[orientation=vertical]/togglegroup:first:border-t-2',
          'group-data-[orientation=vertical]/togglegroup:last:border-b-2',
          'group-data-[orientation=horizontal]/togglegroup:first:border-l-2',
          'group-data-[orientation=horizontal]/togglegroup:last:border-r-2',
        ],
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
);

const toggleGroupVariants = cva([
  'group/togglegroup flex items-center justify-center gap-0 data-[orientation=vertical]:flex-col',
]);

const waveElementVariants = cva([
  'absolute top-0 left-0 z-[-1] animate-wave rounded-md bg-custom-color',
  // FIXME rounded corners in groups
]);

/**
 * Props for the Toggle component.
 * Extends AriaToggleButtonProps and adds styling variants.
 */
interface ToggleProps
  extends AriaToggleButtonProps, VariantProps<typeof toggleVariants> {
  /**
   * Theme color to apply to the toggle button.
   * Controls the background, border, and text colors.
   */
  color?: ThemeColor;
  badge?: React.ReactNode;
  icon?: React.ReactNode;
}

/**
 * Toggle button component that can be switched between on and off states.
 *
 * Renders a button that maintains a toggled/selected state when clicked.
 * Can be used individually or within a ToggleButtonGroup.
 * Built on react-aria-components for robust accessibility support.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Toggle>Bold</Toggle>
 *
 * // Using with isSelected prop
 * <Toggle isSelected={isBold} onChange={setIsBold}>Bold</Toggle>
 *
 * // Custom color
 * <Toggle color="primary">Filter</Toggle>
 * ```
 *
 * @param props - Toggle properties
 * @returns Rendered Toggle component
 */
const Toggle = ({
  className,
  children,
  size,
  color,
  badge,
  icon,
  ...props
}: ToggleProps) => {
  const {
    add: addWaveElement,
    remove: removeWaveElement,
    queue: waveElements,
  } = useQueue<React.ReactNode>([]);

  const styles = {
    ...(color
      ? {
          '--ink-color': `var(--ink-${color})`,
          '--ink-color-darker': `var(--ink-${color}-darker)`,
        }
      : {}),
  } as React.CSSProperties;

  const handlePress = (e: PressEvent) => {
    addWaveElement(
      <div
        key={Date.now()}
        className={waveElementVariants()}
        onAnimationEnd={removeWaveElement}
      >
        &nbsp;
      </div>,
    );

    props.onPress?.(e);
  };

  return (
    <AriaToggleButton
      onPress={handlePress}
      style={styles}
      className={composeRenderProps(className, (className) =>
        cn(
          '@Toggle',
          toggleVariants({
            size,
            className,
          }),
        ),
      )}
      {...props}
    >
      {(
        renderProps: ToggleButtonRenderProps & {
          defaultChildren: React.ReactNode | undefined;
        },
      ) => (
        <Row
          className={cn(
            'flex items-center gap-1.5 text-sm',
            size === 'xl' && 'text-base',
            renderProps.isDisabled && 'opacity-40',
          )}
        >
          {icon && (
            <span className={'contents text-current/50 [&_svg]:size-4'}>
              {icon}
            </span>
          )}
          <span>
            {waveElements}
            {typeof children === 'function' ? children(renderProps) : children}
          </span>
          {isDefined(badge) && (
            <span className="rounded-md bg-neutral/10 px-1 text-xs">
              {badge}
            </span>
          )}
        </Row>
      )}
    </AriaToggleButton>
  );
};

/**
 * ToggleButtonGroup component that groups related toggle buttons.
 *
 * Manages selection state across multiple Toggle buttons.
 * Can be oriented horizontally or vertically.
 *
 * @example
 * ```tsx
 * <ToggleButtonGroup selectionMode="single">
 *   <Toggle>Option 1</Toggle>
 *   <Toggle>Option 2</Toggle>
 *   <Toggle>Option 3</Toggle>
 * </ToggleButtonGroup>
 * ```
 *
 * @param props - ToggleButtonGroup properties
 * @returns Rendered ToggleButtonGroup component
 */
const ToggleButtonGroup = ({
  className,
  children,
  ...props
}: AriaToggleButtonGroupProps) => (
  <AriaToggleButtonGroup
    className={composeRenderProps(className, (className) =>
      cn('@ToggleButtonGroup', toggleGroupVariants(), className),
    )}
    {...props}
  >
    {children}
  </AriaToggleButtonGroup>
);

export { Toggle, ToggleButtonGroup, toggleVariants, waveElementVariants };
export type { ToggleProps };
